import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextFieldProps,
  Tooltip,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import moment, { Moment } from 'moment';
import AdapterMoment from '@mui/lab/AdapterMoment';

import {
  getLessonHistoryState,
  ILessonHistory,
  ILessonHistoryCommonFilterData,
  resetLessonHistoryListData,
  updateLessonHistoryCommonFilterData,
} from '../../../../../store/V2/LessonHistorySlice';
import {
  lessonHistoryListGet,
  TLessonHistoryListGet,
} from '../../../../../store/V2/LessonHistorySlice/api';
import {
  COURSE_ENROLMENT_OPTIONS,
  getCoursesState,
  getEnrolmentCoursesForUsers,
  resetUserEnrolmentCoursesData,
} from '../../../../../store/V2/CoursesSlice';
import {
  getEnrolmentCourseRelatedStudents,
  getEnrolmentCourseRelatedTutors,
} from '../../../../../store/UserSlice/UserApi';
import {
  getUsersState,
  resetEnrolmentCourseRelatedStudentList,
  resetEnrolmentCourseRelatedTutorList,
} from '../../../../../store/UserSlice';
import { useAppSelector } from '../../../../../hooks';
import { getAuthState } from '../../../../../store/AuthSlice';
import {
  checkUserPermission,
  prepareLessonHistoryCSVExportData,
} from '../../../../../helper';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../store/V2/RolesSlice';
import {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../components/V2/Buttons';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../components/Inputs';
import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../../components/V2/Table/type1/useTable';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../../components/V2/Table/type1/TableHeadCustom';
import TablePaginationActions from '../../../../../components/V2/Table/type1/TablePaginationActions';
import CompletedLessonHistoryTableRow from './components/CompletedLessonHistoryTableRow';
import ViewLessonHistoryModal from './modals/ViewLessonHistoryModal';
import TableNoData from '../../../../../components/V2/Empty/TableNoData';
import TableLoader from '../../../../../components/Loaders/TableLoader';
import FilterListModal from './modals/FilterListModal';
import { getCurriculumsV2 } from '../../../../../store/V2/CurriculumSlice';
import {
  TLessonTypes,
  TMeetingSectionPaymentType,
} from '../../../../../store/V2/SectionSlice';
import {
  TMeetingLessonTypeOption,
  TMeetingSectionPaymentTypeOption,
} from '../../../../../constants';
import TutorPaymentDetailsCard from './components/TutorPaymentDetailsCard';
import { getTenantsState } from '../../../../../store/V2/TenantsSlice';

export type TSelectOption = {
  label: string;
  value: string;
};

export type TColumnPermissions = {
  dateTime: boolean;
  students: boolean;
  tutors: boolean;
  recordings: boolean;
  actions: boolean;
  referenceKey: boolean;
  curriculum: boolean;
  subject: boolean;
  grade: boolean;
  lessonType: boolean;
  paymentType: boolean;
  studentJoinTime: boolean;
  tutorJoinTime: boolean;
  course: boolean;
  tutorRate: boolean;
  tutorAmount: boolean;
  tutorPaymentStatus: boolean;
  tutorPenalty: boolean;
  trialStatus: boolean;
};

export type TState = {
  startDate: Moment | null;
  endDate: Moment | null;
  selectedCourse: TSelectOption | null;
  selectedCourseStudent: TSelectOption | null;
  selectedCourseTutor: TSelectOption | null;
  tableHeaderLabels: THeadLabelItem[];
  columnPermissions: TColumnPermissions;
  referenceKey: string;
  selectedCurriculum: {
    label: string;
    value: string;
  } | null;
  selectedGrade: {
    label: string;
    value: string;
  } | null;
  selectedSubject: {
    label: string;
    value: string;
  } | null;
  selectedLessonType: TMeetingLessonTypeOption | null;
  selectedPaymentType: TMeetingSectionPaymentTypeOption | null;
};

export type TFilterData = {
  limit: number;
  offset: number;
  sortKey: string;
  sortValue: number;
  startDate?: string;
  endDate?: string;
  courseId?: string;
  studentId?: string;
  tutorId?: string;
  referenceKey?: string;
  curriculum?: string;
  grade?: string;
  subject?: string;
  lessonType?: TLessonTypes;
  paymentType?: TMeetingSectionPaymentType;
};

const textFieldProps: TextFieldProps = {
  sx: {
    backgroundColor: '#ffffff',
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const defaultListLimit = 30;
const defaultOrderBy = 'start';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};
const defaultStartDateMoment = moment().subtract(1, 'month');
const defaultEndDateMoment = moment().add(1, 'day');

const defaultFilterData: TFilterData = {
  limit: defaultListLimit,
  offset: 0,
  sortKey: defaultOrderBy,
  sortValue: defaultOrder === 'desc' ? -1 : 1,
  startDate: defaultStartDateMoment.startOf('day').toISOString(),
  endDate: defaultEndDateMoment.endOf('day').toISOString(),
};

const initialColumnPermissions: TColumnPermissions = {
  dateTime: false,
  students: false,
  tutors: false,
  recordings: false,
  actions: false,
  referenceKey: false,
  curriculum: false,
  subject: false,
  grade: false,
  lessonType: false,
  paymentType: false,
  studentJoinTime: false,
  tutorJoinTime: false,
  course: true,
  tutorRate: false,
  tutorAmount: false,
  tutorPaymentStatus: false,
  tutorPenalty: false,
  trialStatus: false,
};

const defaultState: TState = {
  startDate: defaultStartDateMoment.clone(),
  endDate: defaultEndDateMoment.clone(),
  selectedCourse: null,
  selectedCourseStudent: null,
  selectedCourseTutor: null,
  tableHeaderLabels: [],
  columnPermissions: { ...initialColumnPermissions },
  referenceKey: '',
  selectedCurriculum: null,
  selectedGrade: null,
  selectedSubject: null,
  selectedLessonType: null,
  selectedPaymentType: null,
};

const CompletedTab: FunctionComponent = () => {
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const lessonHistoryState = useAppSelector(getLessonHistoryState);
  const coursesState = useAppSelector(getCoursesState);
  const usersState = useAppSelector(getUsersState);
  const tenantState = useAppSelector(getTenantsState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;

  // State
  const tableData = useTable(defaultTableData);
  const [state, setState] = useState({ ...defaultState });
  const [filterData, setFilterData] = useState({ ...defaultFilterData });
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [viewLessonModalData, setViewLessonModalData] = useState<{
    open: boolean;
    defaultTabIndex?: number;
    lessonHistory?: ILessonHistory;
  }>({
    open: false,
  });

  const {
    lessonHistoryCommonFilterData,
    lessonHistoryListData,
  } = lessonHistoryState;

  const coursesOptions: TSelectOption[] = [];
  const courseStudentOptions: TSelectOption[] = [];
  const courseTutorOptions: TSelectOption[] = [];
  const hasTutorAmountDataPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      lessonHistoryCompletedTab: [
        USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTOR_AMOUNT,
      ],
    },
  });

  let lessonHistoryList: ILessonHistory[] = [];
  let totalCount = 0;

  if (lessonHistoryListData) {
    totalCount = lessonHistoryListData.totalDocs || 0;

    if (lessonHistoryListData.list && lessonHistoryListData.list.length) {
      lessonHistoryList = lessonHistoryListData.list;
    }
  }

  if (
    coursesState.userEnrolmentCoursesData &&
    coursesState.userEnrolmentCoursesData.list &&
    coursesState.userEnrolmentCoursesData.list.length
  ) {
    coursesState.userEnrolmentCoursesData.list.forEach((item) => {
      coursesOptions.push({
        label: item.title,
        value: item._id,
      });
    });
  }

  if (
    coursesState.userEnrolmentCoursesData &&
    coursesState.userEnrolmentCoursesData.list &&
    coursesState.userEnrolmentCoursesData.list.length
  ) {
    coursesState.userEnrolmentCoursesData.list.forEach((courseItem) => {
      coursesOptions.push({
        label: courseItem.title,
        value: courseItem._id,
      });
    });
  }

  if (
    usersState.enrolmentCourseRelatedStudentList &&
    usersState.enrolmentCourseRelatedStudentList.length
  ) {
    usersState.enrolmentCourseRelatedStudentList.forEach((item) => {
      let _name = item.firstname;

      if (item.lastname) {
        _name += ` ${item.lastname}`;
      }

      courseStudentOptions.push({
        label: _name,
        value: item._id,
      });
    });
  }

  if (
    usersState.enrolmentCourseRelatedTutorList &&
    usersState.enrolmentCourseRelatedTutorList.length
  ) {
    usersState.enrolmentCourseRelatedTutorList.forEach((item) => {
      let _name = item.firstname;

      if (item.lastname) {
        _name += ` ${item.lastname}`;
      }

      courseTutorOptions.push({
        label: _name,
        value: item._id,
      });
    });
  }

  useEffect(() => {
    setupTableColumns();
    getInitialLessonHistoryListData();
    dispatch(getCurriculumsV2());
    dispatch(
      getEnrolmentCoursesForUsers({
        limit: 200,
        offset: 0,
        enrolment: COURSE_ENROLMENT_OPTIONS.ENROLLED,
      })
    );

    dispatch(getEnrolmentCourseRelatedStudents());
    dispatch(getEnrolmentCourseRelatedTutors());

    return () => {
      dispatch(resetLessonHistoryListData());
      dispatch(resetUserEnrolmentCoursesData());
      dispatch(resetEnrolmentCourseRelatedStudentList());
      dispatch(resetEnrolmentCourseRelatedTutorList());
    };
  }, []);

  const setupTableColumns = () => {
    if (authUser) {
      const newTableHeaderLabels: THeadLabelItem[] = [];
      const newColumnPermissions = { ...state.columnPermissions };

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_REFERENCE_KEY,
            ],
          },
        })
      ) {
        newColumnPermissions.referenceKey = true;

        newTableHeaderLabels.push({
          id: 'referenceKey',
          label: 'Reference Key',
          sortable: false,
          minWidth: 100,
          maxWidth: 100,
        });
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_DATE_TIME,
            ],
          },
        })
      ) {
        newColumnPermissions.dateTime = true;

        newTableHeaderLabels.push(
          {
            id: 'start',
            label: 'Date',
            sortable: true,
            minWidth: 120,
            maxWidth: 120,
          },
          {
            id: 'start-time',
            label: 'Start',
            sortable: false,
            minWidth: 100,
            maxWidth: 100,
          },
          {
            id: 'end',
            label: 'End',
            sortable: false,
            minWidth: 100,
            maxWidth: 100,
          },
          {
            id: 'duration',
            label: 'Duration',
            sortable: false,
            minWidth: 100,
            maxWidth: 100,
          }
        );
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_STUDENTS,
            ],
          },
        })
      ) {
        newColumnPermissions.students = true;

        newTableHeaderLabels.push({
          id: 'students',
          label: 'Students',
          sortable: false,
          minWidth: 150,
          maxWidth: 150,
        });
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTORS,
            ],
          },
        })
      ) {
        newColumnPermissions.tutors = true;

        newTableHeaderLabels.push({
          id: 'paymentTutor',
          label: 'Tutor',
          sortable: true,
          minWidth: 150,
          maxWidth: 150,
        });
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_LESSON_TYPE,
            ],
          },
        })
      ) {
        newColumnPermissions.lessonType = true;

        newTableHeaderLabels.push({
          id: 'lessonType',
          label: 'Lesson Type',
          sortable: false,
          minWidth: 100,
          maxWidth: 100,
        });
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_PAYMENT_TYPE,
            ],
          },
        })
      ) {
        newColumnPermissions.paymentType = true;

        newTableHeaderLabels.push({
          id: 'paymentType',
          label: 'Payment Type',
          sortable: false,
          minWidth: 100,
          maxWidth: 100,
        });
      }

      newTableHeaderLabels.push({
        id: 'course',
        label: 'Course',
        sortable: true,
        minWidth: 150,
        maxWidth: 150,
      });

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_CURRICULUM,
            ],
          },
        })
      ) {
        newColumnPermissions.curriculum = true;

        newTableHeaderLabels.push({
          id: 'curriculum',
          label: 'Curriculum',
          sortable: true,
          minWidth: 100,
          maxWidth: 100,
        });
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_SUBJECT,
            ],
          },
        })
      ) {
        newColumnPermissions.subject = true;

        newTableHeaderLabels.push({
          id: 'subject',
          label: 'Subject',
          sortable: true,
          minWidth: 100,
          maxWidth: 100,
        });
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_GRADE,
            ],
          },
        })
      ) {
        newColumnPermissions.grade = true;

        newTableHeaderLabels.push({
          id: 'grade',
          label: 'Grade',
          sortable: true,
          minWidth: 100,
          maxWidth: 100,
        });
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_STUDENT_JOIN_TIME,
            ],
          },
        })
      ) {
        newColumnPermissions.studentJoinTime = true;

        newTableHeaderLabels.push({
          id: 'studentJoinTime',
          label: 'Student Join Time',
          sortable: true,
          minWidth: 130,
          maxWidth: 130,
        });
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTOR_JOIN_TIME,
            ],
          },
        })
      ) {
        newColumnPermissions.tutorJoinTime = true;

        newTableHeaderLabels.push({
          id: 'tutortJoinTime',
          label: 'Tutor Join Time',
          sortable: true,
          minWidth: 130,
          maxWidth: 130,
        });
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTOR_RATE,
            ],
          },
        })
      ) {
        newColumnPermissions.tutorRate = true;

        newTableHeaderLabels.push({
          id: 'tutorRate',
          label: 'Tutor Rate',
          sortable: true,
          minWidth: 100,
          maxWidth: 100,
        });
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTOR_AMOUNT,
            ],
          },
        })
      ) {
        newColumnPermissions.tutorAmount = true;

        newTableHeaderLabels.push({
          id: 'tutorAmount',
          label: 'Tutor Amount',
          sortable: true,
          minWidth: 100,
          maxWidth: 100,
        });
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTOR_PENALTY,
            ],
          },
        })
      ) {
        newColumnPermissions.tutorPenalty = true;

        newTableHeaderLabels.push(
          {
            id: 'tutorPenalty',
            label: 'Tutor Penalty',
            sortable: true,
            minWidth: 100,
            maxWidth: 100,
          },
          {
            id: 'tutorPenaltyAmount',
            label: 'Tutor Penalty Amount',
            sortable: true,
            minWidth: 150,
            maxWidth: 150,
          },
          {
            id: 'tutorPenaltyReason',
            label: 'Tutor Penalty Reason',
            sortable: true,
            minWidth: 150,
            maxWidth: 150,
          }
        );
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TRIAL_STATUS,
            ],
          },
        })
      ) {
        newColumnPermissions.trialStatus = true;

        newTableHeaderLabels.push({
          id: 'trialConversionStatus',
          label: 'Trial Status',
          sortable: false,
          minWidth: 150,
          maxWidth: 150,
        });
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_RECORDINGS,
            ],
          },
        })
      ) {
        newColumnPermissions.recordings = true;

        newTableHeaderLabels.push({
          id: 'zoomRecordings',
          label: 'Recordings',
          sortable: false,
          minWidth: 100,
          maxWidth: 100,
        });
      }

      if (
        checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_ACTIONS,
            ],
          },
        })
      ) {
        newColumnPermissions.actions = true;

        newTableHeaderLabels.push({
          id: 'action',
          label: 'Action',
          sortable: false,
          align: 'right',
        });
      }

      updateStateAttributes({
        tableHeaderLabels: newTableHeaderLabels,
        columnPermissions: newColumnPermissions,
      });
    }
  };

  const getInitialLessonHistoryListData = () => {
    const initialFilterData: TFilterData = {
      ...defaultFilterData,
    };

    const newStateData: Partial<TState> = {};

    if (lessonHistoryCommonFilterData) {
      if (lessonHistoryCommonFilterData.startDate) {
        newStateData.startDate = moment(
          lessonHistoryCommonFilterData.startDate
        );

        initialFilterData.startDate = moment(
          lessonHistoryCommonFilterData.startDate
        )
          .startOf('day')
          .toISOString();
      } else if (lessonHistoryCommonFilterData.startDateCleared) {
        newStateData.startDate = null;
        initialFilterData.startDate = undefined;
      }

      if (lessonHistoryCommonFilterData.endDate) {
        newStateData.endDate = moment(lessonHistoryCommonFilterData.endDate);

        initialFilterData.endDate = moment(
          lessonHistoryCommonFilterData.endDate
        )
          .endOf('day')
          .toISOString();
      } else if (lessonHistoryCommonFilterData.endDateCleared) {
        newStateData.endDate = null;
        initialFilterData.endDate = undefined;
      }

      if (lessonHistoryCommonFilterData.selectedTutor) {
        newStateData.selectedCourseTutor = {
          ...lessonHistoryCommonFilterData.selectedTutor,
        };

        initialFilterData.tutorId =
          lessonHistoryCommonFilterData.selectedTutor.value;
      }

      if (lessonHistoryCommonFilterData.selectedStudent) {
        newStateData.selectedCourseStudent = {
          ...lessonHistoryCommonFilterData.selectedStudent,
        };

        initialFilterData.studentId =
          lessonHistoryCommonFilterData.selectedStudent.value;
      }

      if (lessonHistoryCommonFilterData.selectedCourse) {
        newStateData.selectedCourse = {
          ...lessonHistoryCommonFilterData.selectedCourse,
        };

        initialFilterData.courseId =
          lessonHistoryCommonFilterData.selectedCourse.value;
      }
    }

    getLessonHistoryListData({ ...initialFilterData });
    updateStateAttributes({ ...newStateData });
  };

  const getLessonHistoryListData = async (
    filterDataAttributes: TFilterData
  ) => {
    const apiData: TLessonHistoryListGet = {
      limit: filterDataAttributes.limit,
      offset: filterDataAttributes.offset,
    };

    if (filterDataAttributes.sortKey) {
      apiData.sortKey = filterDataAttributes.sortKey;
    }

    if (filterDataAttributes.sortValue) {
      apiData.sortValue = filterDataAttributes.sortValue;
    }

    if (filterDataAttributes.startDate) {
      apiData.startDate = filterDataAttributes.startDate;
    }

    if (filterDataAttributes.endDate) {
      apiData.endDate = filterDataAttributes.endDate;
    }

    if (filterDataAttributes.courseId) {
      apiData.courseId = filterDataAttributes.courseId;
    }

    if (filterDataAttributes.studentId) {
      apiData.studentId = filterDataAttributes.studentId;
    }

    if (filterDataAttributes.tutorId) {
      apiData.tutorId = filterDataAttributes.tutorId;
    }

    if (filterDataAttributes.referenceKey) {
      apiData.referenceKey = filterDataAttributes.referenceKey;
    }

    if (filterDataAttributes.curriculum) {
      apiData.curriculum = filterDataAttributes.curriculum;
    }

    if (filterDataAttributes.grade) {
      apiData.grade = filterDataAttributes.grade;
    }

    if (filterDataAttributes.subject) {
      apiData.subject = filterDataAttributes.subject;
    }

    if (filterDataAttributes.lessonType) {
      apiData.lessonType = filterDataAttributes.lessonType;
    }

    if (filterDataAttributes.paymentType) {
      apiData.paymentType = filterDataAttributes.paymentType;
    }

    if (hasTutorAmountDataPermission) {
      apiData.includeTutorAmountData = true;
    }

    dispatch(lessonHistoryListGet({ ...apiData }));

    setFilterData({ ...filterDataAttributes });
  };

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSort = (id: string) => {
    let newSortValue = 1;

    if (filterData.sortKey === id && filterData.sortValue === 1) {
      newSortValue = -1;
    }

    getLessonHistoryListData({
      ...filterData,
      sortValue: newSortValue,
      sortKey: id,
    });

    tableData.onSort(id);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    getLessonHistoryListData({
      ...filterData,
      offset: page * filterData.limit,
    });

    tableData.onChangePage(event, page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    getLessonHistoryListData({
      ...filterData,
      offset: 0,
      limit: parseInt(event.target.value, 10),
    });

    tableData.onChangeRowsPerPage(event);
  };

  const toggleFilterModal = () => {
    setFilterModalOpen((prevState) => !prevState);
  };

  const onFiltersApplied = (
    newFilterData: TFilterData,
    newStateAttributes: Partial<TState>
  ) => {
    getLessonHistoryListData({ ...newFilterData });
    updateStateAttributes({ ...newStateAttributes });

    const newLessonHistoryCommonFilterData: ILessonHistoryCommonFilterData = {
      ...(lessonHistoryCommonFilterData || {}),
    };

    if (newStateAttributes) {
      newLessonHistoryCommonFilterData.startDate = newStateAttributes.startDate
        ? newStateAttributes.startDate.toISOString()
        : undefined;

      newLessonHistoryCommonFilterData.startDateCleared = newStateAttributes.startDate
        ? undefined
        : true;

      newLessonHistoryCommonFilterData.endDate = newStateAttributes.endDate
        ? newStateAttributes.endDate.toISOString()
        : undefined;

      newLessonHistoryCommonFilterData.endDateCleared = newStateAttributes.endDate
        ? undefined
        : true;

      newLessonHistoryCommonFilterData.selectedTutor = newStateAttributes.selectedCourseTutor
        ? newStateAttributes.selectedCourseTutor
        : undefined;

      newLessonHistoryCommonFilterData.selectedStudent = newStateAttributes.selectedCourseStudent
        ? newStateAttributes.selectedCourseStudent
        : undefined;

      newLessonHistoryCommonFilterData.selectedCourse = newStateAttributes.selectedCourse
        ? newStateAttributes.selectedCourse
        : undefined;
    }

    dispatch(
      updateLessonHistoryCommonFilterData({
        ...newLessonHistoryCommonFilterData,
      })
    );

    toggleFilterModal();
  };

  const refreshList = () => {
    getLessonHistoryListData({ ...filterData });
  };

  const onExport = () => {
    if (authUser) {
      const link = document.createElement('a');
      const filename = 'lesson-history-export.csv';

      let csv = prepareLessonHistoryCSVExportData({
        columnPermissions: state.columnPermissions,
        lessonHistoryList,
      });

      if (csv == null) return;

      if (!csv.match(/^data:text\/csv/i)) {
        csv = `data:text/csv;charset=utf-8,${csv}`;
      }

      link.setAttribute('href', encodeURI(csv));
      link.setAttribute('download', filename);
      link.click();
    }
  };

  return (
    <div>
      <div style={{ padding: '20px' }}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Grid
            container
            alignItems="center"
            columnSpacing="16px"
            rowSpacing="20px"
          >
            {state.columnPermissions.dateTime ? (
              <>
                <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
                  <FormControl fullWidth>
                    <DatePicker
                      clearable
                      value={state.startDate}
                      label="Start Date"
                      inputFormat="YYYY-MMM-DD"
                      onChange={(date) => {
                        updateStateAttributes({
                          startDate: date,
                        });

                        getLessonHistoryListData({
                          ...filterData,
                          startDate: date
                            ? date.clone().startOf('day').toISOString()
                            : undefined,
                        });

                        dispatch(
                          updateLessonHistoryCommonFilterData({
                            startDate: date ? date.toISOString() : undefined,
                            startDateCleared: date ? undefined : true,
                          })
                        );
                      }}
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...textFieldProps}
                          size="small"
                          placeholder="Start Date"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
                  <FormControl fullWidth>
                    <DatePicker
                      clearable
                      value={state.endDate}
                      label="End Date"
                      inputFormat="YYYY-MMM-DD"
                      onChange={(date) => {
                        updateStateAttributes({
                          endDate: date,
                        });

                        getLessonHistoryListData({
                          ...filterData,
                          endDate: date
                            ? date.clone().endOf('day').toISOString()
                            : undefined,
                        });

                        dispatch(
                          updateLessonHistoryCommonFilterData({
                            endDate: date ? date.toISOString() : undefined,
                            endDateCleared: date ? undefined : true,
                          })
                        );
                      }}
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...textFieldProps}
                          size="small"
                          placeholder="End Date"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              </>
            ) : null}
            {courseStudentOptions.length && state.columnPermissions.students ? (
              <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
                <FormControl fullWidth>
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={state.selectedCourseStudent}
                    options={courseStudentOptions}
                    onChange={(e, value) => {
                      updateStateAttributes({ selectedCourseStudent: value });

                      getLessonHistoryListData({
                        ...filterData,
                        studentId: value ? value.value : undefined,
                      });

                      dispatch(
                        updateLessonHistoryCommonFilterData({
                          selectedStudent: value || undefined,
                        })
                      );
                    }}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        label="Student"
                        placeholder="Student"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            ) : null}
            {courseTutorOptions.length && state.columnPermissions.tutors ? (
              <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
                <FormControl fullWidth>
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={state.selectedCourseTutor}
                    options={courseTutorOptions}
                    onChange={(e, value) => {
                      updateStateAttributes({ selectedCourseTutor: value });

                      getLessonHistoryListData({
                        ...filterData,
                        tutorId: value ? value.value : undefined,
                      });

                      dispatch(
                        updateLessonHistoryCommonFilterData({
                          selectedStudent: value || undefined,
                        })
                      );
                    }}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        label="Tutor"
                        placeholder="Tutor"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            ) : null}
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  size="small"
                  value={state.selectedCourse}
                  options={coursesOptions}
                  onChange={(e, value) => {
                    updateStateAttributes({ selectedCourse: value });

                    getLessonHistoryListData({
                      ...filterData,
                      courseId: value ? value.value : undefined,
                    });

                    dispatch(
                      updateLessonHistoryCommonFilterData({
                        selectedCourse: value || undefined,
                      })
                    );
                  }}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Course"
                      placeholder="Select Course"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <Stack direction="row" alignItems="center" gap="10px">
                <DarkButton
                  variant="contained"
                  color="inherit"
                  sx={{ ...defaultButtonStyles }}
                  onClick={toggleFilterModal}
                  startIcon={<FilterListIcon />}
                >
                  More Filters
                </DarkButton>
                <Tooltip title="Refresh List">
                  <IconButton onClick={refreshList}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </div>

      <Collapse
        in={hasTutorAmountDataPermission && Boolean(lessonHistoryListData)}
      >
        <TutorPaymentDetailsCard
          startDate={state.startDate}
          endDate={state.endDate}
        />
      </Collapse>

      {lessonHistoryList.length ? (
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          p="0 20px 20px 20px"
        >
          <DarkButton
            variant="contained"
            color="inherit"
            onClick={onExport}
            startIcon={<FileUploadIcon />}
            sx={{ ...defaultButtonStyles }}
          >
            Export
          </DarkButton>
        </Stack>
      ) : null}

      <TableContainer
        sx={{ position: 'relative', maxHeight: 'calc(100vh - 250px)' }}
      >
        <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
          <TableHeadCustom
            rowCount={0}
            order={tableData.order}
            orderBy={tableData.orderBy}
            headLabels={state.tableHeaderLabels}
            numSelected={tableData.selected.length}
            onSort={handleSort}
            // onSelectAllRows={(checked) =>
            //   tableData.onSelectAllRows(
            //     checked,
            //     zoomMeetingRequestsList.map((row) => row._id)
            //   )
            // }
          />
          <TableBody>
            {lessonHistoryList.map((row) => {
              // const isSelected = tableData.selected.includes(row._id);

              return (
                <CompletedLessonHistoryTableRow
                  key={row._id}
                  lessonHistory={row}
                  columnPermissions={state.columnPermissions}
                  onView={() =>
                    setViewLessonModalData({
                      open: true,
                      defaultTabIndex: 0,
                      lessonHistory: row,
                    })
                  }
                  onStudentsClick={() =>
                    setViewLessonModalData({
                      open: true,
                      defaultTabIndex: 2,
                      lessonHistory: row,
                    })
                  }
                  onTutorsClick={() =>
                    setViewLessonModalData({
                      open: true,
                      defaultTabIndex: 3,
                      lessonHistory: row,
                    })
                  }
                />
              );
            })}
            {lessonHistoryState.status !== 'loading' &&
            !lessonHistoryList.length ? (
              <TableNoData title="No lesson history found" />
            ) : null}
            {lessonHistoryState.status === 'loading' &&
            !lessonHistoryList.length ? (
              <TableLoader />
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={defaultRowsPerPageOptions}
          component="div"
          count={totalCount}
          rowsPerPage={tableData.rowsPerPage}
          page={tableData.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleRowsPerPageChange}
          ActionsComponent={TablePaginationActions}
        />

        <FormControlLabel
          label="Dense"
          sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
          control={
            <Switch
              checked={tableData.dense}
              onChange={tableData.onChangeDense}
            />
          }
        />
      </Box>
      {viewLessonModalData.open && viewLessonModalData.lessonHistory ? (
        <ViewLessonHistoryModal
          open={viewLessonModalData.open}
          onClose={() => setViewLessonModalData({ open: false })}
          lessonHistoryId={viewLessonModalData.lessonHistory._id}
          defaultTabIndex={viewLessonModalData.defaultTabIndex}
        />
      ) : null}
      {isFilterModalOpen ? (
        <FilterListModal
          open={isFilterModalOpen}
          onClose={toggleFilterModal}
          filters={filterData}
          mainState={state}
          coursesOptions={coursesOptions}
          courseStudentOptions={courseStudentOptions}
          courseTutorOptions={courseTutorOptions}
          columnPermissions={state.columnPermissions}
          onFiltersApplied={onFiltersApplied}
        />
      ) : null}
    </div>
  );
};

export default CompletedTab;
